import { useState, useMemo } from 'react';
import { SyncedGoogleOAuthApp } from '../../types';
import { Shield, AlertTriangle, CheckCircle, Search } from 'lucide-react';

interface GoogleOAuthAppsProps {
  apps: SyncedGoogleOAuthApp[];
}

// Known personal/consumer apps to flag
const PERSONAL_APP_NAMES = [
  'youtube tv', 'doordash', 'wayfair', 'redfin', 'chownow',
  'ticketmaster', 'pinterest', 'yelp', 'coverking', 'hiko social login',
  'fareportal', 'netflix', 'spotify', 'uber', 'lyft', 'venmo',
  'cashapp', 'tiktok', 'instagram', 'snapchat', 'hulu',
];

function isPersonalApp(app: SyncedGoogleOAuthApp): boolean {
  if (app.isPersonalApp) return true;
  const nameLower = app.appName.toLowerCase();
  return PERSONAL_APP_NAMES.some(p => nameLower.includes(p));
}

export default function GoogleOAuthApps({ apps }: GoogleOAuthAppsProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'personal' | 'business'>('all');

  const categorizedApps = useMemo(() => {
    return apps.map(app => ({
      ...app,
      _isPersonal: isPersonalApp(app),
    }));
  }, [apps]);

  const stats = useMemo(() => {
    const personal = categorizedApps.filter(a => a._isPersonal).length;
    const business = categorizedApps.length - personal;
    const uniqueUsers = new Set(categorizedApps.map(a => a.userEmail)).size;
    return { total: categorizedApps.length, personal, business, uniqueUsers };
  }, [categorizedApps]);

  const filteredApps = useMemo(() => {
    let result = categorizedApps;
    if (filterType === 'personal') result = result.filter(a => a._isPersonal);
    if (filterType === 'business') result = result.filter(a => !a._isPersonal);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(a =>
        a.appName.toLowerCase().includes(q) ||
        a.userEmail.toLowerCase().includes(q) ||
        a.userName.toLowerCase().includes(q)
      );
    }
    return result;
  }, [categorizedApps, filterType, searchQuery]);

  if (apps.length === 0) {
    return (
      <div className="card p-6">
        <h3 className="text-lg font-medium text-foreground mb-4">
          <Shield className="h-5 w-5 inline mr-2" />
          OAuth / Third-Party Apps
        </h3>
        <p className="text-muted-foreground text-sm">No OAuth apps data available. Trigger a sync to collect Google Workspace data.</p>
      </div>
    );
  }

  return (
    <div className="card p-6">
      <h3 className="text-lg font-medium text-foreground mb-4">
        <Shield className="h-5 w-5 inline mr-2" />
        OAuth / Third-Party Apps
      </h3>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-muted/50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-foreground">{stats.total}</div>
          <div className="text-xs text-muted-foreground">Total Apps</div>
        </div>
        <div className="bg-muted/50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-foreground">{stats.uniqueUsers}</div>
          <div className="text-xs text-muted-foreground">Users</div>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.business}</div>
          <div className="text-xs text-green-600 dark:text-green-400">Business</div>
        </div>
        <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{stats.personal}</div>
          <div className="text-xs text-orange-600 dark:text-orange-400">Personal</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search apps, users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-9 w-full"
          />
        </div>
        <div className="flex gap-2">
          {(['all', 'business', 'personal'] as const).map(type => (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                filterType === type
                  ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 px-3 font-medium text-muted-foreground">App Name</th>
              <th className="text-left py-2 px-3 font-medium text-muted-foreground">User</th>
              <th className="text-left py-2 px-3 font-medium text-muted-foreground">Scopes</th>
              <th className="text-left py-2 px-3 font-medium text-muted-foreground">Authorized</th>
            </tr>
          </thead>
          <tbody>
            {filteredApps.map((app, idx) => (
              <tr key={`${app.appId}-${app.userEmail}-${idx}`} className="border-b border-border/50 hover:bg-muted/30">
                <td className="py-2 px-3">
                  <div className="flex items-center gap-2">
                    {app._isPersonal ? (
                      <AlertTriangle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                    ) : (
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    )}
                    <span className={app._isPersonal ? 'text-orange-600 dark:text-orange-400 font-medium' : 'text-foreground'}>
                      {app.appName}
                    </span>
                  </div>
                </td>
                <td className="py-2 px-3 text-muted-foreground">
                  <div>{app.userName}</div>
                  <div className="text-xs">{app.userEmail}</div>
                </td>
                <td className="py-2 px-3">
                  <div className="flex flex-wrap gap-1">
                    {app.scopes.slice(0, 3).map((scope, i) => (
                      <span key={i} className="inline-block bg-muted px-1.5 py-0.5 rounded text-xs text-muted-foreground">
                        {scope.length > 30 ? scope.slice(0, 30) + '...' : scope}
                      </span>
                    ))}
                    {app.scopes.length > 3 && (
                      <span className="inline-block bg-muted px-1.5 py-0.5 rounded text-xs text-muted-foreground">
                        +{app.scopes.length - 3} more
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-2 px-3 text-muted-foreground text-xs whitespace-nowrap">
                  {app.authorizedDate
                    ? new Date(app.authorizedDate).toLocaleDateString()
                    : 'Unknown'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredApps.length === 0 && (
          <div className="text-center py-8 text-muted-foreground text-sm">No apps match your filters.</div>
        )}
      </div>
    </div>
  );
}
